import React, { useState } from 'react';
import { Send, Mail, User, MessageSquare } from 'lucide-react';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    marketingConsent: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send email using FormSubmit service
      const response = await fetch('https://formsubmit.co/harry.s.dhindsa@gmail.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          marketingConsent: formData.marketingConsent ? 'Yes' : 'No',
          _subject: `New Contact Form Submission: ${formData.subject}`,
          _template: 'table'
        })
      });

      if (response.ok) {
        setIsSubmitting(false);
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '', marketingConsent: true });

        // Reset success message after 5 seconds
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setIsSubmitting(false);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name Field */}
        <div>
          <label htmlFor="name" className="block text-sm font-bold text-neutral-300 mb-2">
            Full Name *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-neutral-500" />
            </div>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full bg-neutral-900 border-2 border-neutral-800 rounded-lg py-4 pl-12 pr-4 text-white placeholder-neutral-500 focus:border-[#CBB26A] focus:outline-none transition"
              placeholder="John Smith"
            />
          </div>
        </div>

        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-sm font-bold text-neutral-300 mb-2">
            Email Address *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-neutral-500" />
            </div>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full bg-neutral-900 border-2 border-neutral-800 rounded-lg py-4 pl-12 pr-4 text-white placeholder-neutral-500 focus:border-[#CBB26A] focus:outline-none transition"
              placeholder="john@example.com"
            />
          </div>
        </div>

        {/* Subject Field */}
        <div>
          <label htmlFor="subject" className="block text-sm font-bold text-neutral-300 mb-2">
            Subject *
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            required
            className="w-full bg-neutral-900 border-2 border-neutral-800 rounded-lg py-4 px-4 text-white placeholder-neutral-500 focus:border-[#CBB26A] focus:outline-none transition"
            placeholder="Inquiry about Pro tier"
          />
        </div>

        {/* Message Field */}
        <div>
          <label htmlFor="message" className="block text-sm font-bold text-neutral-300 mb-2">
            Message *
          </label>
          <div className="relative">
            <div className="absolute top-4 left-4 pointer-events-none">
              <MessageSquare className="h-5 w-5 text-neutral-500" />
            </div>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows="6"
              className="w-full bg-neutral-900 border-2 border-neutral-800 rounded-lg py-4 pl-12 pr-4 text-white placeholder-neutral-500 focus:border-[#CBB26A] focus:outline-none transition resize-none"
              placeholder="Tell us about your trading needs and how we can help..."
            />
          </div>
        </div>

        {/* Marketing Consent Checkbox */}
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="marketingConsent"
            name="marketingConsent"
            checked={formData.marketingConsent}
            onChange={handleChange}
            className="mt-1 w-5 h-5 bg-neutral-900 border-2 border-neutral-800 rounded text-[#CBB26A] focus:ring-2 focus:ring-[#CBB26A] focus:ring-offset-0 focus:ring-offset-neutral-950"
          />
          <label htmlFor="marketingConsent" className="text-sm text-neutral-400 leading-relaxed">
            I agree to receive marketing emails, product updates, and exclusive offers from HDAlgos. You can unsubscribe at any time.
          </label>
        </div>

        {/* Submit Button */}
        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-black py-5 rounded-lg font-black text-lg hover:from-[#D8C585] hover:to-[#BDA45E] transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-3"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                Sending...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Send Message
              </>
            )}
          </button>
        </div>

        {/* Success Message */}
        {submitStatus === 'success' && (
          <div className="bg-green-950/30 border-2 border-green-900/50 rounded-lg p-4 text-center">
            <p className="text-green-500 font-bold">
              ✓ Message sent successfully! We'll get back to you within 24 hours.
            </p>
          </div>
        )}

        {/* Error Message */}
        {submitStatus === 'error' && (
          <div className="bg-red-950/30 border-2 border-red-900/50 rounded-lg p-4 text-center">
            <p className="text-red-500 font-bold">
              ✗ Something went wrong. Please try again or email us directly at harry.s.dhindsa@gmail.com
            </p>
          </div>
        )}
      </form>

      {/* Contact Info */}
      <div className="mt-16 grid md:grid-cols-3 gap-8 text-center">
        <div className="bg-neutral-900 border-2 border-neutral-800 p-6 rounded-xl">
          <div className="w-12 h-12 bg-[#CBB26A]/30 border-2 border-[#CBB26A]/30 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Mail className="w-6 h-6 text-[#CBB26A]" />
          </div>
          <div className="text-sm text-neutral-500 mb-2">Email</div>
          <div className="text-white font-bold">support@hdalgos.com</div>
        </div>

        <div className="bg-neutral-900 border-2 border-neutral-800 p-6 rounded-xl">
          <div className="w-12 h-12 bg-[#CBB26A]/30 border-2 border-[#CBB26A]/30 rounded-lg flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-6 h-6 text-[#CBB26A]" />
          </div>
          <div className="text-sm text-neutral-500 mb-2">Response Time</div>
          <div className="text-white font-bold">Within 24 Hours</div>
        </div>

        <div className="bg-neutral-900 border-2 border-neutral-800 p-6 rounded-xl">
          <div className="w-12 h-12 bg-[#CBB26A]/30 border-2 border-[#CBB26A]/30 rounded-lg flex items-center justify-center mx-auto mb-4">
            <User className="w-6 h-6 text-[#CBB26A]" />
          </div>
          <div className="text-sm text-neutral-500 mb-2">Support</div>
          <div className="text-white font-bold">Professional Traders</div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
